import { createTheme, darken, getContrastRatio, lighten } from '@mui/material';

const Colors = {
  Coral: '#E76F51',
  Denim: '#14374A',
  Fossil: '#787878',
  Granola: '#F4B44F',
  Sea: '#4EB0AD',
  Red: '#D0342C',
  Sapphire: '#5cb5b3',
  Shadow: '#3d3d3d',
  White: '#fcfcfc',
  Black: '#333333',
};

const Fonts = {
  Inter: 'Inter',
  Merriweather: '"Merriweather", serif',
  Montserrat: '"Montserrat", sans-serif',
  Opensans: '"Open Sans", sans-serif',
};

const themeSettings = {
  legacy: {
    dark: { main: Colors.Fossil, contrastText: Colors.White },
    neutral: { main: Colors.White, contrastText: Colors.Sapphire },
    green: { main: Colors.Sapphire, contrastText: Colors.White },
    white: { main: Colors.White, contrastText: Colors.Sapphire },
    yellow: { main: Colors.Granola, contrastText: Colors.Fossil },
    lightGrey: { main: lighten(Colors.Fossil, 0.2), contrastText: Colors.Sapphire },
    gray: { main: Colors.Fossil, contrastText: Colors.Sapphire },
    red: { light: Colors.White, main: Colors.Red, dark: Colors.Denim, contrastText: Colors.Sapphire },
    blue: { main: Colors.Denim, contrastText: Colors.Fossil },
    black: { main: Colors.Black, contrastText: Colors.White },
    warning: { main: Colors.Red, contrastText: Colors.White },
    primary: {
      main: Colors.Sapphire,
      light: lighten(Colors.Sapphire, 0.3),
      dark: darken(Colors.Sapphire, 0.1),
      contrastText: Colors.White,
    },
    secondary: { main: Colors.White, contrastText: Colors.Sapphire },
    info: { main: Colors.Sapphire, contrastText: Colors.White },
    text: { primary: Colors.Fossil, secondary: Colors.Fossil },
    highlight: { main: Colors.Sapphire, contrastText: Colors.Black },
    textAccent: { main: darken(Colors.Sapphire, 0.2), contrastText: lighten(Colors.Sapphire, 0.9) },
    background: Colors.White,
    typography: {
      fontFamily: Fonts.Opensans,
      body1: { fontFamily: Fonts.Opensans, fontSize: '1rem', color: Colors.Fossil, fontWeight: '400' },
      body2: { fontFamily: Fonts.Opensans, fontSize: '.75rem', color: Colors.Fossil, fontWeight: '400' },
      quote: Fonts.Merriweather,
      subHeading: { fontFamily: Fonts.Opensans, color: Colors.Fossil, fontSize: '20px', fontWeight: 'bold' },
      h1: {
        fontFamily: Fonts.Opensans,
        fontSize: '1.5rem',
        fontWeight: '700',
        color: Colors.Fossil,
        textDecoration: 'underline',
        textDecorationColor: Colors.Sapphire,
        textUnderlineOffset: '8px',
      },
      h2: { fontFamily: Fonts.Opensans, fontSize: '1.3rem', fontWeight: '700' },
      h3: { fontFamily: Fonts.Opensans, fontSize: '1.1rem', fontWeight: '700' },
      h4: { fontFamily: Fonts.Opensans, fontSize: '1.0rem', fontWeight: '700' },
      h5: { fontFamily: Fonts.Opensans, fontSize: '1.1rem', fontWeight: '700' },
      h6: { fontFamily: Fonts.Opensans, fontSize: '.8rem', fontWeight: '700' },
      caption: { fontFamily: Fonts.Opensans, fontSize: '.875rem', fontWeight: '700' },
    },
    custom: {
      themeColor: darken(Colors.White, 0.02),
      bodyTextColor: darken(Colors.White, 0.52),
      bodyBackgroundColor: Colors.White,
      button: {
        borderRadius: '3px',
        primary: { textColor: Colors.White, backgroundColor: Colors.Sapphire, hoverBackgroundColor: darken(Colors.Sapphire, 0.1) },
        secondary: { textColor: Colors.White, backgroundColor: Colors.Sapphire, hoverBackgroundColor: darken(Colors.Sapphire, 0.1) },
        tab: { textColor: Colors.Fossil, backgroundColor: 'transparent', hoverTextColor: Colors.White, hoverBackgroundColor: Colors.Sapphire },
        mid: { textColor: Colors.Fossil, backgroundColor: darken(Colors.White, 0.1), hoverBackgroundColor: darken(Colors.White, 0.1) },
        warning: { textColor: Colors.White, backgroundColor: Colors.Red, hoverBackgroundColor: darken(Colors.Red, 0.1) },
      },
      sidebar: {
        badgeColor: Colors.White,
        badgeBackgroundColor: Colors.Sapphire,
        closeIconColor: Colors.Fossil,
        closeIconBackgroundColor: darken(Colors.White, 0.08),
        closeIconHoverColor: Colors.Fossil,
        closeIconHoverBackgroundColor: darken(Colors.White, 0.13),
        iconTextColor: Colors.Shadow,
        iconBackgroundColor: Colors.Sapphire,
        menuItemColor: Colors.Fossil,
        menuItemActiveColor: Colors.Sapphire,
        menuItemHoverColor: Colors.Fossil,
        menuItemHoverBackgroundColor: lighten(Colors.Sapphire, 0.6),
      },
      lightBorderColor: darken(Colors.White, 0.1),
      lightTextColor: darken(Colors.White, 0.52),
      iconFillColor: 'transparent',
      lightHoverBackground: lighten(Colors.Sapphire, 0.3),
      darkHoverBackground: lighten(Colors.Sapphire, 0.7),
      logo: '/logos/GreenLogoNoBack.png',
    },
  },
  classic: {
    dark: { main: Colors.Denim, contrastText: Colors.White },
    neutral: { main: Colors.White, contrastText: Colors.Denim },
    green: { main: Colors.Sea, contrastText: Colors.Denim },
    white: { main: Colors.White, contrastText: Colors.Denim },
    yellow: { main: darken(Colors.Granola, 0.05), contrastText: Colors.Denim },
    lightGrey: { main: darken(Colors.White, 0.1), contrastText: Colors.Denim },
    gray: { main: darken(Colors.White, 0.7), contrastText: Colors.White },
    red: { main: Colors.Coral, contrastText: Colors.Denim },
    blue: { main: Colors.Denim, contrastText: Colors.Sea },
    black: { main: Colors.Black, contrastText: Colors.White },
    warning: { main: Colors.Red, contrastText: Colors.White },
    primary: {
      main: lighten(Colors.Denim, 0.1),
      light: lighten(Colors.Denim, 0.6),
      dark: Colors.Denim,
      contrastText: lighten(Colors.Denim, 0.99),
    },
    secondary: { main: Colors.Granola, contrastText: Colors.Denim },
    info: { main: darken(Colors.Sea, 0.09), contrastText: Colors.Denim },
    text: { primary: lighten(Colors.Denim, 0.15), secondary: lighten(Colors.Denim, 0.32) },
    highlight: { main: darken(Colors.Granola, 0.25), contrastText: Colors.Denim },
    textAccent: { main: darken(Colors.Sea, 0.3), contrastText: lighten(Colors.Sea, 0.9) },
    background: Colors.White,
    typography: {
      fontFamily: Fonts.Inter,
      body1: { fontFamily: Fonts.Inter, fontSize: '1rem', color: lighten(Colors.Denim, 0.15), fontWeight: '400' },
      body2: { fontFamily: Fonts.Inter, fontSize: '.75rem', color: lighten(Colors.Denim, 0.15), fontWeight: '400' },
      quote: Fonts.Merriweather,
      subHeading: { fontFamily: Fonts.Montserrat, fontSize: '1.3rem' },
      h1: {
        fontFamily: Fonts.Montserrat,
        fontSize: '1.8rem',
        fontWeight: '600',
        textDecoration: 'underline',
        textDecorationColor: Colors.Granola,
        textUnderlineOffset: '8px',
      },
      h2: { fontFamily: Fonts.Montserrat, fontSize: '1.4rem', fontWeight: '600' },
      h3: { fontFamily: Fonts.Montserrat, fontSize: '1.2rem', fontWeight: '600' },
      h4: { fontFamily: Fonts.Montserrat, fontSize: '1.1rem', fontWeight: '600' },
      h5: { fontFamily: Fonts.Montserrat, fontSize: '1.1rem', fontWeight: '600' },
      h6: { fontFamily: Fonts.Montserrat, fontSize: '.8rem', fontWeight: '700' },
      caption: { fontFamily: Fonts.Inter, fontSize: '.875rem', fontWeight: '700' },
    },
    custom: {
      themeColor: Colors.Denim,
      bodyTextColor: lighten(Colors.Denim, 0.15),
      bodyBackgroundColor: Colors.White,
      button: {
        borderRadius: '3px',
        primary: { textColor: Colors.White, backgroundColor: Colors.Denim, hoverBackgroundColor: darken(Colors.Denim, 0.1) },
        secondary: { textColor: Colors.Denim, backgroundColor: Colors.Granola, hoverBackgroundColor: darken(Colors.Granola, 0.05) },
        tab: { textColor: Colors.Denim, backgroundColor: 'transparent', hoverTextColor: Colors.Denim, hoverBackgroundColor: Colors.Sea },
        mid: { textColor: Colors.Fossil, backgroundColor: Colors.Fossil, hoverBackgroundColor: darken(Colors.Fossil, 0.1) },
        warning: { textColor: Colors.White, backgroundColor: Colors.Red, hoverBackgroundColor: darken(Colors.Red, 0.1) },
      },
      sidebar: {
        badgeColor: Colors.Denim,
        badgeBackgroundColor: Colors.Granola,
        closeIconColor: Colors.White,
        closeIconBackgroundColor: lighten(Colors.Denim, 0.1),
        closeIconHoverColor: Colors.White,
        closeIconHoverBackgroundColor: darken(Colors.Granola, 0.2),
        iconTextColor: Colors.Denim,
        iconBackgroundColor: Colors.Granola,
        menuItemColor: Colors.White,
        menuItemActiveColor: Colors.Granola,
        menuItemHoverColor: Colors.White,
        menuItemHoverBackgroundColor: lighten(Colors.Denim, 0.1),
      },
      lightBorderColor: darken(Colors.White, 0.1),
      lightTextColor: Colors.White,
      iconFillColor: Colors.White,
      lightHoverBackground: lighten(Colors.Denim, 0.3),
      darkHoverBackground: lighten(Colors.Denim, 0.7),
      logo: '/logos/LogoDark.png',
      lightLogo: '/logos/LogoLight.png',
    },
  },
};

const Themes = {
  Legacy: 'legacy',
  Classic: 'classic',
};

export const myTheme = themeSettings[Themes.Classic];

export const typographyStyles = {
  subHeading: myTheme.typography.subHeading,
  visuallyHidden: {
    clip: 'rect(0, 0, 0, 0)',
    height: 1,
    overflow: 'hidden',
    position: 'absolute',
    whiteSpace: 'nowrap',
    width: 1,
  },
};

export const appTheme = {
  palette: {
    dark: myTheme.dark,
    neutral: myTheme.neutral,
    green: myTheme.green,
    white: myTheme.white,
    yellow: myTheme.yellow,
    lightGrey: myTheme.lightGrey,
    gray: myTheme.gray,
    red: myTheme.red,
    blue: myTheme.blue,
    black: myTheme.black,
    highlight: myTheme.highlight,
    textAccent: myTheme.textAccent,
  },
  custom: myTheme.custom,
  typography: {
    subHeading: myTheme.typography.subHeading,
    visuallyHidden: typographyStyles.visuallyHidden,
  },
};

export const theme = createTheme({
  palette: {
    mode: 'light',
    primary: {
      main: myTheme.primary.main,
      light: myTheme.primary.light,
      dark: myTheme.primary.dark,
      contrastText: myTheme.primary.contrastText,
    },
    secondary: {
      main: myTheme.secondary.main,
      contrastText: myTheme.secondary.contrastText,
    },
    error: {
      main: '#e42d31',
      dark: '#b0171a',
      contrastText: '#fff',
    },
    success: {
      main: '#21884a',
      contrastText: '#fff',
    },
    info: {
      main: myTheme.info.main,
      contrastText: myTheme.info.contrastText,
    },
    warning: {
      main: myTheme.warning.main,
      contrastText: myTheme.warning.contrastText,
    },
    text: {
      primary: myTheme.text.primary,
      secondary: myTheme.text.secondary,
    },
    background: {
      default: myTheme.background,
    },
  },
  typography: {
    fontFamily: myTheme.typography.fontFamily,
    body1: myTheme.typography.body1,
    body2: myTheme.typography.body2,
    h1: myTheme.typography.h1,
    h2: myTheme.typography.h2,
    h3: myTheme.typography.h3,
    h4: myTheme.typography.h4,
    h5: myTheme.typography.h5,
    h6: myTheme.typography.h6,
    caption: myTheme.typography.caption,
  },
  components: {
    MuiAutocomplete: {
      styleOverrides: {
        inputRoot: {
          '&.Mui-disabled': {
            backgroundColor: '#f6f8f9',
          },
        },
      },
    },
    MuiAvatar: {
      styleOverrides: {
        root: {
          backgroundColor: myTheme.green.main,
        },
      },
    },
    MuiButton: {
      defaultProps: {
        disableRipple: true,
        disableElevation: true,
      },
      styleOverrides: {
        root: {
          fontFamily: myTheme.typography.fontFamily,
          textTransform: 'none',
          fontWeight: '600',
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        label: {
          fontFamily: myTheme.typography.fontFamily,
        },
      },
    },
    MuiDialogContent: {
      defaultProps: {
        dividers: true,
      },
    },
    MuiDialogActions: {
      styleOverrides: {
        root: {
          borderTop: '#d8d8d8 solid 1px',
          backgroundColor: '#eeeeee',
        },
      },
    },
    MuiDialogTitle: {
      defaultProps: {
        variant: 'h3',
      },
    },
    MuiIconButton: {
      defaultProps: {
        disableRipple: true,
      },
    },
    MuiInputLabel: {
      styleOverrides: {
        root: {
          marginBottom: 4,
          color: myTheme.text.primary,
        },
        asterisk: {
          color: 'red',
        },
      },
    },
    MuiListItemText: {
      defaultProps: {
        primaryTypographyProps: { color: 'text.primary' },
        secondaryTypographyProps: { color: 'text.primary' },
      },
    },
    MuiPaper: {
      styleOverrides: {
        elevation1: {
          boxShadow:
            'rgba(149, 157, 165, 0.2) 0px 4px 8px, rgba(0, 0, 0, 0.06) 0px 0px 0px 1px',
        },
      },
    },
    MuiPopover: {
      styleOverrides: {
        paper: {
          boxShadow:
            'rgba(149, 157, 165, 0.2) 0px 4px 8px, rgba(0, 0, 0, 0.06) 0px 0px 0px 1px',
        },
      },
    },
    MuiSelect: {
      styleOverrides: {
        select: {
          '&.Mui-disabled': {
            backgroundColor: '#f6f8f9',
          },
        },
      },
    },
    MuiTab: {
      styleOverrides: {
        root: {
          textTransform: 'none',
          color: myTheme.text.primary,
        },
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          '& .MuiInputBase-root': {
            color: myTheme.text.primary,
          },
        },
      },
    },
    MuiInputBase: {
      styleOverrides: {
        input: {
          ':disabled': {
            backgroundColor: '#f6f8f9',
          },
        },
      },
    },
    MuiTooltip: {
      styleOverrides: {
        tooltip: {
          fontFamily: myTheme.typography.body1.fontFamily,
          padding: '20px',
          backgroundColor: myTheme.neutral.main,
          color: myTheme.text.primary,
          border: `1px solid ${lighten(myTheme.text.primary, 0.8)}`,
        },
      },
    },
    MuiInputAdornment: {
      styleOverrides: {
        root: {
          color: myTheme.text.primary,
        },
      },
    },
    MuiFormHelperText: {
      styleOverrides: {
        root: {
          color: myTheme.text.primary,
        },
      },
    },
    MuiLink: {
      styleOverrides: {
        root: {
          color: darken(myTheme.green.main, 0.3),
          transition: 'color 0.2s ease',
          '&:hover': {
            color: myTheme.text.primary,
            cursor: 'pointer',
          },
        },
      },
    },
  },
});

export const getProfileAvatarColor = () =>
  getContrastRatio(myTheme.primary.main, myTheme.secondary.main) > 2.9
    ? myTheme.secondary.main
    : myTheme.secondary.contrastText;
